import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Clock, Loader2, FileSpreadsheet, Hand, User, Pencil, Save, X as XIcon, Plus, CalendarPlus, Trash2, Repeat } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
};

/**
 * View-only modal showing one staff member's schedule across all days of
 * an event. Each row shows date + start–end + source badge (Excel vs Manual)
 * and any flags (Split / Extra / Night). The admin uses this purely to verify
 * what's currently scheduled — editing is done via the existing SET HOURS flow.
 */
export default function StaffScheduleViewDialog({ open, onOpenChange, group, staff, canEdit = true }) {
  const [loading, setLoading] = useState(false);
  const [staffRow, setStaffRow] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);
  // Inline-edit state — only one row at a time, kept simple on purpose.
  const [editingShiftId, setEditingShiftId] = useState(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [saving, setSaving] = useState(false);
  // Add-day inline mode — when set to a date string, that day's row shows
  // start/end inputs so admin can assign + schedule in one go.
  const [addingDate, setAddingDate] = useState(null);
  const [addStart, setAddStart] = useState('');
  const [addEnd, setAddEnd] = useState('');
  const [addingBusy, setAddingBusy] = useState(false);
  // When set to a date string, a confirmation row is shown for removing this
  // staff member from that day. Kept simple — no dialog, inline prompt.
  const [removingDate, setRemovingDate] = useState(null);
  const [removingBusy, setRemovingBusy] = useState(false);
  // Replace-staff inline mode — swaps the employee on ONE date only.
  const [replacingShift, setReplacingShift] = useState(null);
  const [replaceUserId, setReplaceUserId] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [replaceBusy, setReplaceBusy] = useState(false);

  const fetchSchedule = useCallback(async () => {
    if (!group?.shifts?.length || !staff?.user_id) return;
    setLoading(true);
    try {
      const ids = group.shifts.map((s) => s.id).join(',');
      const res = await axios.get(`${API}/admin/event-schedule`, { params: { open_shift_ids: ids } });
      const me = (res.data?.staff || []).find((s) => s.user_id === staff.user_id);
      // Dedupe shifts_by_date defensively so the modal never shows two rows
      // for the same date even when the underlying DB has leftover duplicates.
      if (me?.shifts_by_date) {
        const cleaned = {};
        Object.entries(me.shifts_by_date).forEach(([d, arr]) => {
          const seen = new Set();
          cleaned[d] = (arr || []).filter((sh) => {
            const key = sh.shift_id || `${sh.start_time}|${sh.end_time}|${sh.hours}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
        me.shifts_by_date = cleaned;
      }
      setStaffRow(me || null);
      setEventInfo(res.data?.event || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load schedule');
    } finally {
      setLoading(false);
    }
  }, [group, staff]);

  useEffect(() => {
    if (open) fetchSchedule();
    if (!open) {
      setStaffRow(null);
      setEventInfo(null);
      setEditingShiftId(null);
      setAddingDate(null);
      setRemovingDate(null);
    }
  }, [open, fetchSchedule]);

  const startAdd = (d) => {
    // Pre-fill with the open_shift's default times if available.
    const openShift = group.shifts.find((s) => s.date === d);
    setAddingDate(d);
    setAddStart(openShift?.start_time || '');
    setAddEnd(openShift?.end_time || '');
  };

  const cancelAdd = () => {
    setAddingDate(null);
    setAddStart('');
    setAddEnd('');
  };

  const saveAdd = async (date) => {
    const openShift = group.shifts.find((s) => s.date === date);
    if (!openShift) {
      toast.error('Open shift not found for this date');
      return;
    }
    setAddingBusy(true);
    try {
      // 1) Assign the staff to that day's open shift. Default times come
      //    straight from the open_shift — no input required.
      const notify = window.confirm('Send email notification to the affected staff?\n\nOK = send email now\nCancel = save WITHOUT sending any email');
      await axios.post(`${API}/open-shifts/${openShift.id}/add-staff?notify=${notify}`, {
        user_id: staff.user_id,
      });
      // 2) If the admin entered custom times AND both fields are set,
      //    apply them on top of the default shift.
      if (addStart && addEnd) {
        const reFetch = await axios.get(`${API}/admin/event-schedule`, {
          params: { open_shift_ids: group.shifts.map((s) => s.id).join(',') },
        });
        const me = (reFetch.data?.staff || []).find((s) => s.user_id === staff.user_id);
        const newShift = (me?.shifts_by_date?.[date] || [])[0];
        if (newShift?.shift_id
            && (newShift.start_time !== addStart || newShift.end_time !== addEnd)) {
          await axios.put(`${API}/shifts/${newShift.shift_id}`, {
            start_time: addStart,
            end_time: addEnd,
          });
        }
      }
      toast.success(`Added to ${date}`);
      cancelAdd();
      await fetchSchedule();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add staff to this day');
    } finally {
      setAddingBusy(false);
    }
  };

  const startEdit = (sh) => {
    setEditingShiftId(sh.shift_id);
    setEditStart(sh.start_time || '');
    setEditEnd(sh.end_time || '');
  };

  const cancelEdit = () => {
    setEditingShiftId(null);
    setEditStart('');
    setEditEnd('');
  };

  const saveEdit = async (shiftId) => {
    if (!editStart || !editEnd) {
      toast.error('Both start and end time are required');
      return;
    }
    setSaving(true);
    try {
      const notify = window.confirm('Send email notification to the affected staff?\n\nOK = send email now\nCancel = save WITHOUT sending any email');
      await axios.put(`${API}/shifts/${shiftId}?notify=${notify}`, {
        start_time: editStart,
        end_time: editEnd,
      });
      toast.success(notify ? 'Shift updated — email sent' : 'Shift updated (no email sent)');
      cancelEdit();
      await fetchSchedule();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update shift');
    } finally {
      setSaving(false);
    }
  };

  // Remove this staff member from the open_shift covering `date`. The other
  // staff scheduled on that day are unaffected — only this user's claim and
  // their per-day `shifts` row are deleted (handled server-side by
  // /remove-staff). Used when admin added the wrong day by mistake.
  const confirmRemove = (d) => setRemovingDate(d);
  const cancelRemove = () => setRemovingDate(null);
  const removeFromDay = async (date) => {
    const openShift = group.shifts.find((s) => s.date === date);
    if (!openShift) {
      toast.error('Open shift not found for this date');
      return;
    }
    setRemovingBusy(true);
    try {
      await axios.post(
        `${API}/open-shifts/${openShift.id}/remove-staff?user_id=${staff.user_id}&single_day=true`
      );
      toast.success(`Removed from ${date}`);
      setRemovingDate(null);
      await fetchSchedule();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove staff from this day');
    } finally {
      setRemovingBusy(false);
    }
  };

  const startReplace = async (sh) => {
    setReplacingShift(sh.shift_id);
    setReplaceUserId('');
    if (staffOptions.length === 0) {
      try {
        const [emp, sup] = await Promise.all([
          axios.get(`${API}/admin/employees`),
          axios.get(`${API}/admin/supervisors`),
        ]);
        const opts = [...(emp.data || []), ...(sup.data || [])]
          .map((u) => ({ id: u.id, name: u.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setStaffOptions(opts);
      } catch {
        toast.error('Could not load staff list');
      }
    }
  };

  const saveReplace = async (shiftId) => {
    if (!replaceUserId) {
      toast.error('Choose a replacement staff member');
      return;
    }
    setReplaceBusy(true);
    try {
      const res = await axios.post(`${API}/admin/events/shift/${shiftId}/replace-staff`, {
        new_user_id: replaceUserId,
      });
      toast.success(res.data.message);
      setReplacingShift(null);
      await fetchSchedule();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not replace staff');
    } finally {
      setReplaceBusy(false);
    }
  };

  if (!staff) return null;

  const dates = eventInfo?.dates || [];
  const totalShifts = staffRow?.total_shifts || 0;
  const totalHours = staffRow?.total_hours || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-barlow tracking-wider uppercase">
            <User className="w-5 h-5 text-primary" />
            {staff.user_name}
          </DialogTitle>
          <DialogDescription>
            {eventInfo ? (
              <span className="block">
                Schedule for <strong className="text-amber-300">{eventInfo.position || 'Event'}</strong>
                {eventInfo.location ? <> — {eventInfo.location}</> : null}
                {dates.length > 0 && (
                  <> ({dates.length} day{dates.length !== 1 ? 's' : ''})</>
                )}
              </span>
            ) : (
              'Loading…'
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
          </div>
        ) : !staffRow ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            This staff has no shifts scheduled yet for this event.
            <br />
            <span className="text-xs">Click their name in the Manage Staff list to set hours.</span>
          </div>
        ) : (
          <>
            {/* Top stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="border border-border rounded-sm p-2 bg-muted/30">
                <div className="text-xl font-bold text-foreground">{totalShifts}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Shifts</div>
              </div>
              <div className="border border-border rounded-sm p-2 bg-muted/30">
                <div className="text-xl font-bold text-primary">{totalHours.toFixed(1)}h</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total hours</div>
              </div>
              <div className="border border-border rounded-sm p-2 bg-muted/30">
                <div className="text-xl font-bold text-foreground">{dates.length}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Event days</div>
              </div>
            </div>

            {/* Day-by-day list (every event day, even when this staff isn't on it) */}
            <div className="flex-1 overflow-auto border border-border rounded-sm mt-2 divide-y divide-border/40">
              {dates.map((d) => {
                const shifts = staffRow.shifts_by_date?.[d] || [];
                return (
                  <div key={d} className="flex gap-3 p-3">
                    <div className="w-28 shrink-0 text-sm font-medium">{formatDateShort(d)}</div>
                    <div className="flex-1 space-y-1">
                      {shifts.length === 0 ? (
                        addingDate === d ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            <Input
                              type="time"
                              value={addStart}
                              onChange={(e) => setAddStart(e.target.value)}
                              className="h-7 w-[110px] text-sm"
                              data-testid={`add-start-${d}`}
                            />
                            <span className="text-muted-foreground">–</span>
                            <Input
                              type="time"
                              value={addEnd}
                              onChange={(e) => setAddEnd(e.target.value)}
                              className="h-7 w-[110px] text-sm"
                              data-testid={`add-end-${d}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => saveAdd(d)}
                              disabled={addingBusy}
                              className="h-7 px-2 bg-primary text-primary-foreground hover:bg-yellow-300"
                              data-testid={`add-save-${d}`}
                            >
                              {addingBusy
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelAdd}
                              disabled={addingBusy}
                              className="h-7 px-2"
                              data-testid={`add-cancel-${d}`}
                            >
                              <XIcon className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground italic">Not scheduled</span>
                            {canEdit && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => startAdd(d)}
                                className="h-6 px-2 text-[11px] text-primary hover:text-primary hover:bg-primary/10 ml-auto"
                                data-testid={`add-day-${d}`}
                              >
                                <CalendarPlus className="w-3 h-3 mr-1" />
                                Add day
                              </Button>
                            )}
                          </div>
                        )
                      ) : (
                        shifts.map((sh, i) => {
                          const isEditing = editingShiftId === sh.shift_id;
                          return (
                          <div key={i} className="flex items-center gap-2 flex-wrap">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            {isEditing ? (
                              <>
                                <Input
                                  type="time"
                                  value={editStart}
                                  onChange={(e) => setEditStart(e.target.value)}
                                  className="h-7 w-[110px] text-sm"
                                  data-testid={`edit-start-${sh.shift_id}`}
                                />
                                <span className="text-muted-foreground">–</span>
                                <Input
                                  type="time"
                                  value={editEnd}
                                  onChange={(e) => setEditEnd(e.target.value)}
                                  className="h-7 w-[110px] text-sm"
                                  data-testid={`edit-end-${sh.shift_id}`}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => saveEdit(sh.shift_id)}
                                  disabled={saving}
                                  className="h-7 px-2 bg-primary text-primary-foreground hover:bg-yellow-300"
                                  data-testid={`edit-save-${sh.shift_id}`}
                                >
                                  {saving
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEdit}
                                  disabled={saving}
                                  className="h-7 px-2"
                                  data-testid={`edit-cancel-${sh.shift_id}`}
                                >
                                  <XIcon className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <span className="font-mono text-sm">
                                  {sh.start_time || '?'} – {sh.end_time || '?'}
                                </span>
                                <span className="text-xs text-muted-foreground">({(sh.hours || 0).toFixed(1)}h)</span>
                                {sh.source === 'excel' ? (
                                  <Badge variant="outline" className="text-[9px] py-0 h-4 border-green-500/40 text-green-300 bg-green-500/5" title="Imported from Excel">
                                    <FileSpreadsheet className="w-2.5 h-2.5 mr-0.5" />
                                    Excel
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[9px] py-0 h-4 border-blue-500/40 text-blue-300 bg-blue-500/5" title="Added manually">
                                    <Hand className="w-2.5 h-2.5 mr-0.5" />
                                    Manual
                                  </Badge>
                                )}
                                {sh.is_split && (
                                  <Badge variant="outline" className="text-[9px] py-0 h-4 border-amber-500/40 text-amber-300">Split</Badge>
                                )}
                                {sh.is_extra && (
                                  <Badge variant="outline" className="text-[9px] py-0 h-4 border-purple-500/40 text-purple-300">Extra</Badge>
                                )}
                                {sh.end_time && sh.start_time && sh.end_time <= sh.start_time && (
                                  <Badge variant="outline" className="text-[9px] py-0 h-4 border-indigo-500/40 text-indigo-300">Night</Badge>
                                )}
                                {canEdit && sh.shift_id && (
                                  <div className="ml-auto flex items-center gap-1">
                                    {removingDate === d ? (
                                      <>
                                        <span className="text-[11px] text-destructive">Remove?</span>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => removeFromDay(d)}
                                          disabled={removingBusy}
                                          className="h-6 px-2 text-[11px] text-destructive hover:text-destructive hover:bg-destructive/10"
                                          data-testid={`confirm-remove-${d}`}
                                        >
                                          {removingBusy
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : 'Yes'}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={cancelRemove}
                                          disabled={removingBusy}
                                          className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                                          data-testid={`cancel-remove-${d}`}
                                        >
                                          No
                                        </Button>
                                      </>
                                    ) : replacingShift === sh.shift_id ? (
                                      <>
                                        <select
                                          value={replaceUserId}
                                          onChange={(e) => setReplaceUserId(e.target.value)}
                                          className="h-7 text-xs bg-background border border-border rounded-sm px-1 max-w-[170px]"
                                          data-testid={`replace-select-${sh.shift_id}`}
                                        >
                                          <option value="">Replace with…</option>
                                          {staffOptions.filter((o) => o.id !== staff.user_id).map((o) => (
                                            <option key={o.id} value={o.id}>{o.name}</option>
                                          ))}
                                        </select>
                                        <Button
                                          size="sm"
                                          onClick={() => saveReplace(sh.shift_id)}
                                          disabled={replaceBusy}
                                          className="h-6 px-2 text-[11px] bg-primary text-primary-foreground hover:bg-yellow-300"
                                          data-testid={`replace-save-${sh.shift_id}`}
                                        >
                                          {replaceBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm'}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setReplacingShift(null)}
                                          disabled={replaceBusy}
                                          className="h-6 px-2 text-[11px]"
                                          data-testid={`replace-cancel-${sh.shift_id}`}
                                        >
                                          <XIcon className="w-3 h-3" />
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => startEdit(sh)}
                                          className="h-6 px-2 text-[11px] text-primary hover:text-primary hover:bg-primary/10"
                                          data-testid={`edit-shift-${sh.shift_id}`}
                                        >
                                          <Pencil className="w-3 h-3 mr-1" />
                                          Edit
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => startReplace(sh)}
                                          className="h-6 px-2 text-[11px] text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                          title={`Replace ${staff.user_name} on ${d} only`}
                                          data-testid={`replace-shift-${sh.shift_id}`}
                                        >
                                          <Repeat className="w-3 h-3 mr-1" />
                                          Replace
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => confirmRemove(d)}
                                          className="h-6 px-2 text-[11px] text-destructive hover:text-destructive hover:bg-destructive/10"
                                          title={`Remove ${staff.user_name} from ${d}`}
                                          data-testid={`remove-day-${d}`}
                                        >
                                          <Trash2 className="w-3 h-3 mr-1" />
                                          Delete
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="flex justify-end pt-2 border-t border-border">
          <Button onClick={() => onOpenChange?.(false)} className="bg-primary text-primary-foreground hover:bg-yellow-300">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
